import { useTranslation } from 'react-i18next'
import PageLayout from 'layouts/page-layout/PageLayout.tsx'
import { useStoreSelector } from 'hooks/store.ts'
import { selectProducerId } from 'store/contact/selectors.ts'
import styles from './Overview.module.scss'
import { routes } from 'types/routes.ts'
import MarketPricesContent from 'pages/app/market-prices/market-prices-content/MarketPricesContent.tsx'
import OverviewCard from './overview-cards/OverviewCard.tsx'
import InvoicesOverviewCard from './overview-cards/invoices/InvoicesOverviewCard.tsx'
import ProducerOverviewCard from './overview-cards/producer/ProducerOverviewCard.tsx'
import ReferralOverviewCard from './overview-cards/referral/ReferralOverviewCard.tsx'
import WelcomeOverviewCard from './overview-cards/welcome/WelcomeOverviewCard.tsx'
import BillShockOverviewCard from './overview-cards/bill-shock/BillShockOverviewCard.tsx'
import UsageOverviewCard from './overview-cards/usage/UsageOverviewCard.tsx'
import { useRef } from 'react'
import SupportOverviewCard from 'pages/app/overview/overview-cards/support/SupportOverviewCard.tsx'
import GuaranteeOfOriginOverviewCard from 'pages/app/overview/overview-cards/guarantee-of-origin/GuaranteeOfOriginOverviewCard.tsx'
import PhishingCard from './overview-cards/phishing/PhishingCard.tsx'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import SalesSurveyOverviewCard from 'pages/app/overview/overview-cards/sales-survey/SalesSurveyOverviewCard.tsx'
import { selectCurrentAccount, selectCurrentBillingContract, selectIsProducerAccount } from 'store/contact/slice.ts'
import { Heading } from '@boltenergy-be/design-system'
import AddressSwitcher from 'components/address-switcher/AdressSwitcher.tsx'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { SMALL_DESKTOP_BREAKPOINT } from 'constants/viewport.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'

// Set to true to show the phishing card
const phishingCardEnabled = false
const PHISHING_COOKIE_SLUG = 'phishing-info-card'

const Overview = () => {
  const showPhishingCard = phishingCardEnabled
    ? !Cookies.get(PHISHING_COOKIE_SLUG) ||
      (!!Cookies.get(PHISHING_COOKIE_SLUG) && dayjs(Cookies.get(PHISHING_COOKIE_SLUG)).isAfter(dayjs().subtract(1, 'day')))
    : false

  // Redux store
  const { contact } = useStoreSelector((store) => store.contact)
  const accessRights = useStoreSelector((state) => state.accessRights)
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const { producerId: accountProducerId } = useStoreSelector(selectCurrentAccount)
  const billingContract = useStoreSelector(selectCurrentBillingContract)

  // Selected data
  const producerId = isProducer && accountProducerId ? accountProducerId : selectProducerId(billingContract)

  // Window size
  const { isSmaller } = useWindowSize(SMALL_DESKTOP_BREAKPOINT)

  // Is on screen
  const layoutRef = useRef<HTMLDivElement>(null)
  const secondCardRef = useRef<HTMLDivElement>(null)

  // i18n
  const { t } = useTranslation('overview')

  return (
    <PageLayout
      seoTitle={t(($) => $.title, {
        name: contact?.firstName || ''
      })}
      title={
        <div className={styles['title-with-switcher']}>
          <Heading as="h1" variant="h3" serif>
            {t(($) => $.title, {
              name: contact?.firstName || ''
            })}
          </Heading>
          {!isSmaller && <AddressSwitcher containerClassName={styles['switcher-container']} className={styles.switcher} />}
        </div>
      }
      classes={{ body: styles.content }}
      ref={layoutRef}
    >
      {/* Bill shock */}
      {!isProducer && <BillShockOverviewCard email={contact?.email || ''} {...{ billingContract, accessRights }} />}

      {/* Welcome/Beta */}
      <WelcomeOverviewCard />

      {/* Phishing */}
      {showPhishingCard && <PhishingCard CookieSlug={PHISHING_COOKIE_SLUG} />}

      {/* Referral */}
      {!isProducer && <ReferralOverviewCard ref={secondCardRef} />}

      {/* Missing Guarantee of Origins*/}
      {isProducer && <GuaranteeOfOriginOverviewCard />}

      {/* Market prices */}
      {accessRights[ACCESS_RIGHT_KEYS.marketPrices].todayTomorrow.canAccess && (
        <OverviewCard
          button={{
            label: t(($) => $.dynamicTariff.button),
            href: routes.MARKET_PRICES
          }}
        >
          <OverviewCard.Title>{t(($) => $.dynamicTariff.title)}</OverviewCard.Title>
          <MarketPricesContent chartLayoutClassName={styles['desktop-chart-wrapper']} compact />
        </OverviewCard>
      )}

      {/* Consumption */}
      <UsageOverviewCard {...{ accessRights }} />

      {/* Invoice status */}
      <InvoicesOverviewCard />

      {/* Producer */}
      <ProducerOverviewCard {...{ producerId }} />

      {/* Sales survey card */}
      <SalesSurveyOverviewCard />

      {/* Contact */}
      <SupportOverviewCard />
    </PageLayout>
  )
}

export default Overview
